import { pluginFactory } from '@atlaskit/editor-common/utils';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import {
	EditorState,
	PluginKey,
	Transaction,
	type SafeStateField,
} from '@atlaskit/editor-prosemirror/state';

// eslint-disable-next-line import/order
import type { AiQuickPromptAction, AiQuickPromptState, EligibleRange } from './types';
// eslint-disable-next-line import/order
import type { Dispatch } from '@atlaskit/editor-common/event-dispatcher';
// eslint-disable-next-line import/order
import type { Command } from '@atlaskit/editor-common/types';

export const pluginKey: PluginKey<AiQuickPromptState> = new PluginKey<AiQuickPromptState>(
	'aiQuickPromptPlugin',
);

const MAX_PREVIOUS_SHOWN_TEXTS = 1;

export const initialState: AiQuickPromptState = {
	isPromptVisible: false,
	eligibleRange: undefined,
	previousShownTexts: [],
};

export function reducer(
	state: AiQuickPromptState,
	action: AiQuickPromptAction,
): AiQuickPromptState {
	switch (action.type) {
		case 'SHOW_PROMPT': {
			if (!action.range) {
				return state;
			}

			let previousShownTexts = !!action.shownText?.trim()
				? [action.shownText.trim(), ...state.previousShownTexts]
				: state.previousShownTexts;
			if (previousShownTexts.length > MAX_PREVIOUS_SHOWN_TEXTS) {
				previousShownTexts = previousShownTexts.slice(0, MAX_PREVIOUS_SHOWN_TEXTS);
			}

			return {
				...state,
				isPromptVisible: true,
				eligibleRange: action.range,
				previousShownTexts,
			};
		}
		case 'HIDE_PROMPT': {
			if (!state.isPromptVisible && state.eligibleRange === undefined) {
				return state;
			}
			return {
				...state,
				isPromptVisible: false,
				eligibleRange: undefined,
			};
		}
		default:
			return state;
	}
}

const dest = pluginFactory(pluginKey, reducer, {
	mapping: (tr, pluginState) => {
		let newState = pluginState;

		if (newState.eligibleRange) {
			const newFrom = tr.mapping.map(newState.eligibleRange.from);
			const newTo = tr.mapping.map(newState.eligibleRange.to);
			if (newFrom !== newState.eligibleRange.from || newTo !== newState.eligibleRange.to) {
				newState = {
					...newState,
					eligibleRange: { from: newFrom, to: newTo },
				};
			}
		}

		return newState;
	},

	// Hide the prompt when the selection changes
	// idleDetector will be stopped in the view update callback
	onSelectionChanged: (_tr, pluginState, editorState) => {
		let newState = pluginState;

		if (newState.isPromptVisible && newState.eligibleRange) {
			const { from, to } = newState.eligibleRange;
			const sel = editorState.selection;
			if (sel.from !== from || sel.to !== to) {
				newState = {
					...newState,
					isPromptVisible: false,
					eligibleRange: undefined,
				};
			}
		}

		return newState;
	},
});
export const createPluginState: (
	dispatch: Dispatch,
	initialState: AiQuickPromptState | ((state: EditorState) => AiQuickPromptState),
) => SafeStateField<AiQuickPromptState> = dest.createPluginState;
export const createCommand: <A = AiQuickPromptAction>(
	action: A | ((state: Readonly<EditorState>) => false | A),
	transform?: (tr: Transaction, state: EditorState) => Transaction,
) => Command = dest.createCommand;
export const getPluginState: (state: EditorState) => AiQuickPromptState = dest.getPluginState;

export const createShowPromptCommand = ({
	range,
	shownText,
}: {
	range: EligibleRange;
	shownText: string;
}): Command =>
	createCommand<AiQuickPromptAction>(() => {
		return {
			type: 'SHOW_PROMPT',
			range,
			shownText,
		};
	});

export const createHidePromptCommand = (): Command =>
	createCommand<AiQuickPromptAction>({
		type: 'HIDE_PROMPT',
	});
